import { useEffect, useRef, useState } from "react";
import { Bell, Broom, Check, DownloadSimple, Package, Plus, ShieldCheck, Thermometer, ThermometerCold, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1";
const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { k: "oggi", label: "Oggi" },
  { k: "temperature", label: "Temperature" },
  { k: "pulizie", label: "Pulizie" },
  { k: "merce", label: "Merce" },
  { k: "scadenze", label: "Scadenze" },
];
const FREQ_BADGE = { giornaliera: "bg-sky-100 text-sky-800", settimanale: "bg-violet-100 text-violet-800", mensile: "bg-zinc-100 text-zinc-600" };
const SCAD_BADGE = { scaduto: "bg-rose-600 text-white", urgente: "bg-amber-100 text-amber-800", in_scadenza: "bg-zinc-100 text-zinc-600" };
const SCAD_LABEL = { scaduto: "SCADUTO", urgente: "URGENTE", in_scadenza: "in scadenza" };
const TIPO_LABEL = { frigo: "Frigo", cella: "Cella", congelatore: "Congelatore", cottura: "Cottura", altro: "Altro" };

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtD = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";

function Card({ title, icon, children, testid }) {
  const Icon = icon;
  return (
    <section className="bg-white border border-zinc-200 rounded-lg" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
        <Icon size={17} weight="bold" className="text-zinc-700" />
        <h2 className="font-display font-semibold text-zinc-900 text-sm">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function Haccp() {
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user?.role);
  const [tab, setTab] = useState("oggi");
  const [oggi, setOggi] = useState(null);
  const [devices, setDevices] = useState([]);
  const [tempLogs, setTempLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [cleanLogs, setCleanLogs] = useState([]);
  const [goods, setGoods] = useState([]);
  const [products, setProducts] = useState([]);
  const [temps, setTemps] = useState({}); // device_id -> string
  // form config (manager)
  const [devForm, setDevForm] = useState({ nome: "", tipo: "frigo", min_temp: 0, max_temp: 4 });
  const [taskForm, setTaskForm] = useState({ nome: "", zona: "", frequenza: "giornaliera", prodotto: "" });
  const [goodsForm, setGoodsForm] = useState({ fornitore: "", prodotto: "", lotto: "", ddt: "", temperatura: "", esito: "conforme", nota: "" });
  const [prodForm, setProdForm] = useState({ nome: "", lotto: "", scadenza: "", quantita: "", posizione: "" });
  const [exp, setExp] = useState({ tipo: "temperature", da: "", a: "" });
  const [avvisi, setAvvisi] = useState(null);
  const [firma, setFirma] = useState(null);
  const [firmando, setFirmando] = useState(false);
  const padRef = useRef(null);
  const drawingRef = useRef(false);
  const drawnRef = useRef(false);

  const loadOggi = () => api.get("/haccp/oggi").then((r) => setOggi(r.data)).catch(() => {});
  const loadDevices = () => api.get("/haccp/devices").then((r) => setDevices(r.data)).catch(() => {});
  const loadTempLogs = () => api.get("/haccp/temperature").then((r) => setTempLogs(r.data)).catch(() => {});
  const loadTasks = () => api.get("/haccp/cleaning/tasks").then((r) => setTasks(r.data)).catch(() => {});
  const loadCleanLogs = () => api.get("/haccp/cleaning/logs").then((r) => setCleanLogs(r.data)).catch(() => {});
  const loadGoods = () => api.get("/haccp/goods").then((r) => setGoods(r.data)).catch(() => {});
  const loadProducts = () => api.get("/haccp/products").then((r) => setProducts(r.data)).catch(() => {});

  useEffect(() => {
    loadOggi();
    if (isManager) {
      api.get("/settings").then((r) => setAvvisi({
        avvisi_attivi: false, ora_promemoria: "10:00", giorni_scadenze: 1,
        email_avvisi: "", ...((r.data || {}).haccp || {}),
      })).catch(() => {});
      loadFirma();
    }
  }, []);
  useEffect(() => {
    if (tab === "temperature") { loadDevices(); loadTempLogs(); }
    if (tab === "pulizie") { loadTasks(); loadCleanLogs(); }
    if (tab === "merce") loadGoods();
    if (tab === "scadenze") loadProducts();
  }, [tab]);

  const registraTemp = async (d) => {
    const v = parseFloat(String(temps[d.id] || "").replace(",", "."));
    if (isNaN(v)) { toast.error("Inserisci la temperatura"); return; }
    try {
      const r = await api.post("/haccp/temperature", { device_id: d.id, temperatura: v });
      if (r.data.fuori_soglia) toast.error(`ATTENZIONE: ${v}°C fuori soglia (${d.min_temp} / ${d.max_temp} °C)`);
      else toast.success(`${d.nome}: ${v}°C registrata`);
      setTemps({ ...temps, [d.id]: "" });
      loadOggi();
    } catch (e) { toast.error(apiError(e)); }
  };

  const puliziaFatta = async (t) => {
    try {
      await api.post(`/haccp/cleaning/${t.id}/done`, {});
      toast.success(`"${t.nome}" segnata come fatta`);
      loadOggi();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addDevice = async () => {
    try {
      await api.post("/haccp/devices", { ...devForm, min_temp: parseFloat(devForm.min_temp), max_temp: parseFloat(devForm.max_temp) });
      toast.success("Dispositivo aggiunto");
      setDevForm({ nome: "", tipo: "frigo", min_temp: 0, max_temp: 4 });
      loadDevices(); loadOggi();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addTask = async () => {
    try {
      await api.post("/haccp/cleaning/tasks", taskForm);
      toast.success("Attività aggiunta");
      setTaskForm({ nome: "", zona: "", frequenza: "giornaliera", prodotto: "" });
      loadTasks(); loadOggi();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addGoods = async () => {
    try {
      await api.post("/haccp/goods", {
        ...goodsForm, temperatura: goodsForm.temperatura === "" ? null : parseFloat(goodsForm.temperatura),
      });
      toast.success("Controllo merce registrato");
      setGoodsForm({ fornitore: "", prodotto: "", lotto: "", ddt: "", temperatura: "", esito: "conforme", nota: "" });
      loadGoods();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addProduct = async () => {
    try {
      await api.post("/haccp/products", prodForm);
      toast.success("Prodotto aggiunto allo scadenziario");
      setProdForm({ nome: "", lotto: "", scadenza: "", quantita: "", posizione: "" });
      loadProducts(); loadOggi();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (path, reload, msg) => {
    try { await api.delete(path); toast.success(msg); reload(); loadOggi(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const expUrl = `${API}/api/haccp/export.pdf?tipo=${exp.tipo}${exp.da ? `&da=${exp.da}` : ""}${exp.a ? `&a=${exp.a}` : ""}`;

  const saveAvvisi = async () => {
    try {
      await api.patch("/settings", { haccp: {
        avvisi_attivi: !!avvisi.avvisi_attivi,
        ora_promemoria: avvisi.ora_promemoria || "10:00",
        giorni_scadenze: parseInt(avvisi.giorni_scadenze) || 1,
        email_avvisi: avvisi.email_avvisi || "",
      } });
      toast.success("Avvisi automatici salvati");
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadFirma = () => api.get("/haccp/signature")
    .then((r) => { setFirma(r.data); setFirmando(!r.data.exists); }).catch(() => {});

  const padPos = (e) => {
    const cv = padRef.current;
    const r = cv.getBoundingClientRect();
    return [(e.clientX - r.left) * (cv.width / r.width), (e.clientY - r.top) * (cv.height / r.height)];
  };
  const padStart = (e) => {
    const ctx = padRef.current.getContext("2d");
    ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.strokeStyle = "#18181b";
    const [x, y] = padPos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
    drawingRef.current = true; drawnRef.current = true;
  };
  const padMove = (e) => {
    if (!drawingRef.current) return;
    const ctx = padRef.current.getContext("2d");
    const [x, y] = padPos(e);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const padEnd = () => { drawingRef.current = false; };
  const padClear = () => {
    const cv = padRef.current;
    cv.getContext("2d").clearRect(0, 0, cv.width, cv.height);
    drawnRef.current = false;
  };
  const saveFirma = async () => {
    if (!drawnRef.current) { toast.error("Disegna la firma prima di salvare"); return; }
    try {
      await api.post("/haccp/signature", { image_base64: padRef.current.toDataURL("image/png") });
      toast.success("Firma salvata: sarà apposta su ogni pagina dei registri PDF");
      drawnRef.current = false;
      loadFirma();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delFirma = async () => {
    try {
      await api.delete("/haccp/signature");
      toast.success("Firma eliminata");
      loadFirma();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!oggi) return <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>;

  return (
    <div className="space-y-4 max-w-4xl" data-testid="haccp-page">
      <div className="flex items-start gap-3">
        <ShieldCheck size={28} weight="bold" className="text-emerald-600 mt-1 shrink-0" />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">HACCP</h1>
          <p className="text-xs text-zinc-500">Registro digitale sicurezza alimentare: temperature, pulizie, merce in entrata, scadenze. Export PDF per le ispezioni.</p>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-zinc-200" data-testid="haccp-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`haccp-tab-${t.k}`}
            className={`px-4 h-10 text-sm font-semibold whitespace-nowrap border-b-2 transition ${tab === t.k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "oggi" && (
        <div className="space-y-4" data-testid="haccp-oggi">
          <Card title="Temperature da registrare" icon={Thermometer} testid="haccp-oggi-temp">
            {oggi.dispositivi.length === 0 && (
              <p className="text-sm text-zinc-400">Nessun dispositivo configurato.{isManager && " Aggiungilo nel tab Temperature."}</p>
            )}
            <div className="divide-y divide-zinc-100">
              {oggi.dispositivi.map((d) => (
                <div key={d.id} className="py-2.5 flex items-center gap-2 flex-wrap" data-testid={`haccp-temp-row-${d.id}`}>
                  <div className="flex-1 min-w-32">
                    <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                      {d.nome}
                      <span className="text-[10px] font-bold text-zinc-400 uppercase">{TIPO_LABEL[d.tipo] || d.tipo}</span>
                      {d.registrata_oggi && <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-0.5"><Check size={11} weight="bold" /> oggi</span>}
                    </div>
                    <div className="text-xs text-zinc-500">
                      soglia {d.min_temp} / {d.max_temp} °C
                      {d.last_temp != null && (
                        <span className={d.last_fuori_soglia ? "text-rose-600 font-bold" : ""}>
                          {" · "}ultima {d.last_temp}°C ({fmtDT(d.last_at)})
                        </span>
                      )}
                    </div>
                  </div>
                  <input inputMode="decimal" value={temps[d.id] || ""}
                    onChange={(e) => setTemps({ ...temps, [d.id]: e.target.value })}
                    placeholder="°C" className="h-11 w-20 px-2 rounded-md border border-zinc-300 text-sm text-center"
                    data-testid={`haccp-temp-input-${d.id}`} />
                  <button onClick={() => registraTemp(d)} data-testid={`haccp-temp-save-${d.id}`}
                    className="h-11 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold hover:bg-zinc-800 active:scale-95 transition">
                    Registra
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Pulizie e sanificazione" icon={Broom} testid="haccp-oggi-pulizie">
            {oggi.pulizie.length === 0 && (
              <p className="text-sm text-zinc-400">Nessuna attività configurata.{isManager && " Aggiungila nel tab Pulizie."}</p>
            )}
            <div className="divide-y divide-zinc-100">
              {oggi.pulizie.map((t) => (
                <div key={t.id} className="py-2.5 flex items-center gap-2" data-testid={`haccp-clean-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2 flex-wrap">
                      {t.nome}
                      <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ${FREQ_BADGE[t.frequenza] || FREQ_BADGE.giornaliera}`}>{t.frequenza}</span>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {[t.zona, t.prodotto].filter(Boolean).join(" · ") || "—"}
                      {t.last_at && <> · ultima: {fmtDT(t.last_at)}{t.last_by ? ` (${t.last_by})` : ""}</>}
                    </div>
                  </div>
                  {t.fatta_oggi ? (
                    <span className="text-xs font-bold text-emerald-600 flex items-center gap-1" data-testid={`haccp-clean-done-${t.id}`}>
                      <Check size={14} weight="bold" /> Fatta
                    </span>
                  ) : t.da_fare ? (
                    <button onClick={() => puliziaFatta(t)} data-testid={`haccp-clean-do-${t.id}`}
                      className="h-10 px-4 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:scale-95 transition">
                      Segna fatta
                    </button>
                  ) : (
                    <span className="text-[11px] text-zinc-400">non dovuta</span>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card title="Scadenze prodotti" icon={Warning} testid="haccp-oggi-scadenze">
            {oggi.scadenze.length === 0 && <p className="text-sm text-zinc-400">Nessun prodotto in scadenza nei prossimi 7 giorni.</p>}
            <div className="divide-y divide-zinc-100">
              {oggi.scadenze.map((p) => (
                <div key={p.id} className="py-2 flex items-center gap-2" data-testid={`haccp-exp-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900">{p.nome}</div>
                    <div className="text-xs text-zinc-500">scade {fmtD(p.scadenza)}{p.lotto ? ` · lotto ${p.lotto}` : ""}{p.posizione ? ` · ${p.posizione}` : ""}</div>
                  </div>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${SCAD_BADGE[p.stato]}`}>{SCAD_LABEL[p.stato]}</span>
                </div>
              ))}
            </div>
          </Card>

          {isManager && avvisi && (
            <Card title="Avvisi automatici" icon={Bell} testid="haccp-avvisi">
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-zinc-700 font-semibold">
                  <input type="checkbox" checked={!!avvisi.avvisi_attivi}
                    onChange={(e) => setAvvisi({ ...avvisi, avvisi_attivi: e.target.checked })}
                    className="w-4 h-4 accent-zinc-900" data-testid="haccp-avvisi-attivi" />
                  Avvisi attivi
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className={labelCls}>Promemoria temperature dopo le</label>
                    <input type="time" value={avvisi.ora_promemoria}
                      onChange={(e) => setAvvisi({ ...avvisi, ora_promemoria: e.target.value })}
                      className={inputCls} data-testid="haccp-avvisi-ora" />
                  </div>
                  <div>
                    <label className={labelCls}>Preavviso scadenze (giorni)</label>
                    <input type="number" min="0" max="7" value={avvisi.giorni_scadenze}
                      onChange={(e) => setAvvisi({ ...avvisi, giorni_scadenze: e.target.value })}
                      className={inputCls} data-testid="haccp-avvisi-giorni" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>Email avvisi (vuota = admin)</label>
                    <input type="email" value={avvisi.email_avvisi}
                      onChange={(e) => setAvvisi({ ...avvisi, email_avvisi: e.target.value })}
                      placeholder="titolare@ristorante.it" className={inputCls} data-testid="haccp-avvisi-email" />
                  </div>
                </div>
                <p className="text-[11px] text-zinc-400">
                  Canali: email + notifica push nel gestionale. Allarme immediato se una temperatura registrata è fuori soglia.
                  Gli avvisi partono una volta al giorno per tipo, senza ripetizioni.
                </p>
                <button onClick={saveAvvisi} data-testid="haccp-avvisi-save"
                  className="h-11 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold hover:bg-zinc-800 active:scale-95 transition">
                  Salva avvisi
                </button>
              </div>
            </Card>
          )}

          {isManager && (
            <Card title="Export registri per ispezione (PDF)" icon={DownloadSimple} testid="haccp-export">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <label className={labelCls}>Registro</label>
                  <select value={exp.tipo} onChange={(e) => setExp({ ...exp, tipo: e.target.value })}
                    className={inputCls} data-testid="haccp-export-tipo">
                    <option value="temperature">Temperature</option>
                    <option value="pulizie">Pulizie</option>
                    <option value="merce">Merce in entrata</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Dal</label>
                  <input type="date" value={exp.da} onChange={(e) => setExp({ ...exp, da: e.target.value })}
                    className={inputCls} data-testid="haccp-export-da" />
                </div>
                <div>
                  <label className={labelCls}>Al</label>
                  <input type="date" value={exp.a} onChange={(e) => setExp({ ...exp, a: e.target.value })}
                    className={inputCls} data-testid="haccp-export-a" />
                </div>
                <a href={expUrl} target="_blank" rel="noreferrer" data-testid="haccp-export-btn"
                  className="h-11 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-zinc-800">
                  <DownloadSimple size={15} weight="bold" /> Scarica PDF
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 space-y-2">
                <label className={labelCls}>Firma digitale del registro</label>
                {firma?.exists && !firmando ? (
                  <div className="flex items-center gap-3 flex-wrap" data-testid="haccp-firma-view">
                    <img src={`data:image/png;base64,${firma.image}`} alt="Firma del responsabile"
                      className="h-14 border border-zinc-200 rounded-md bg-white px-2" data-testid="haccp-firma-img" />
                    <div className="text-xs text-zinc-500 flex-1" data-testid="haccp-firma-status">
                      Firmato da <strong>{firma.firmato_da || "—"}</strong> il {fmtDT(firma.signed_at)}.
                      Apposta automaticamente su ogni pagina dei PDF esportati.
                    </div>
                    <button onClick={() => setFirmando(true)} data-testid="haccp-firma-rifirma"
                      className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-bold text-zinc-600 hover:bg-zinc-50">
                      Rifirma
                    </button>
                    <button onClick={delFirma} data-testid="haccp-firma-delete"
                      className="h-9 px-3 rounded-md border border-rose-200 text-xs font-bold text-rose-600 hover:bg-rose-50">
                      Elimina
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="haccp-firma-pad">
                    <canvas ref={padRef} width={560} height={160}
                      onPointerDown={padStart} onPointerMove={padMove}
                      onPointerUp={padEnd} onPointerLeave={padEnd}
                      className="w-full max-w-md h-32 border border-zinc-300 rounded-md bg-white touch-none cursor-crosshair"
                      data-testid="haccp-firma-canvas" />
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={saveFirma} data-testid="haccp-firma-save"
                        className="h-10 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold hover:bg-zinc-800 active:scale-95 transition">
                        Salva firma
                      </button>
                      <button onClick={padClear} data-testid="haccp-firma-clear"
                        className="h-10 px-4 rounded-md border border-zinc-300 text-xs font-bold text-zinc-600 hover:bg-zinc-50">
                        Pulisci
                      </button>
                      {firma?.exists && (
                        <button onClick={() => setFirmando(false)} data-testid="haccp-firma-annulla"
                          className="h-10 px-4 rounded-md border border-zinc-300 text-xs font-bold text-zinc-600 hover:bg-zinc-50">
                          Annulla
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-400">Firma con il dito o il mouse: verrà apposta su ogni pagina dei registri PDF con data e dicitura di firma digitale.</p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}

      {tab === "temperature" && (
        <div className="space-y-4" data-testid="haccp-temperature">
          {isManager && (
            <Card title="Nuovo dispositivo" icon={Plus} testid="haccp-device-form">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                <div className="col-span-2">
                  <label className={labelCls}>Nome</label>
                  <input value={devForm.nome} onChange={(e) => setDevForm({ ...devForm, nome: e.target.value })}
                    placeholder="es. Frigo banco" className={inputCls} data-testid="haccp-dev-nome" />
                </div>
                <div>
                  <label className={labelCls}>Tipo</label>
                  <select value={devForm.tipo} onChange={(e) => setDevForm({ ...devForm, tipo: e.target.value })}
                    className={inputCls} data-testid="haccp-dev-tipo">
                    {Object.entries(TIPO_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Min °C</label>
                  <input type="number" step="0.5" value={devForm.min_temp}
                    onChange={(e) => setDevForm({ ...devForm, min_temp: e.target.value })}
                    className={inputCls} data-testid="haccp-dev-min" />
                </div>
                <div>
                  <label className={labelCls}>Max °C</label>
                  <input type="number" step="0.5" value={devForm.max_temp}
                    onChange={(e) => setDevForm({ ...devForm, max_temp: e.target.value })}
                    className={inputCls} data-testid="haccp-dev-max" />
                </div>
              </div>
              <button onClick={addDevice} data-testid="haccp-dev-add"
                className="mt-3 h-11 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                <Plus size={14} weight="bold" /> Aggiungi dispositivo
              </button>
            </Card>
          )}
          <Card title="Dispositivi" icon={ThermometerCold} testid="haccp-devices">
            <div className="divide-y divide-zinc-100">
              {devices.map((d) => (
                <div key={d.id} className="py-2.5 flex items-center gap-2" data-testid={`haccp-dev-row-${d.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-zinc-900">{d.nome}</div>
                    <div className="text-xs text-zinc-500">{TIPO_LABEL[d.tipo]} · soglia {d.min_temp} / {d.max_temp} °C</div>
                  </div>
                  {isManager && (
                    <button onClick={() => del(`/haccp/devices/${d.id}`, loadDevices, "Dispositivo eliminato")}
                      className="h-9 w-9 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center"
                      data-testid={`haccp-dev-del-${d.id}`}><Trash size={15} /></button>
                  )}
                </div>
              ))}
              {devices.length === 0 && <p className="text-sm text-zinc-400">Nessun dispositivo.</p>}
            </div>
          </Card>
          <Card title="Ultime registrazioni" icon={Thermometer} testid="haccp-temp-logs">
            <div className="divide-y divide-zinc-100">
              {tempLogs.slice(0, 20).map((l) => (
                <div key={l.id} className="py-2 flex items-center gap-2 text-sm">
                  <span className="text-zinc-500 text-xs w-24 shrink-0">{fmtDT(l.at)}</span>
                  <span className="flex-1 font-semibold text-zinc-900">{l.device_nome}</span>
                  <span className={l.fuori_soglia ? "text-rose-600 font-black" : "text-zinc-700 font-bold"}>{l.temperatura}°C</span>
                  {l.fuori_soglia && <span className="text-[10px] font-black text-rose-600">FUORI SOGLIA</span>}
                  <span className="text-xs text-zinc-400">{l.by}</span>
                </div>
              ))}
              {tempLogs.length === 0 && <p className="text-sm text-zinc-400">Nessuna registrazione.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === "pulizie" && (
        <div className="space-y-4" data-testid="haccp-pulizie">
          {isManager && (
            <Card title="Nuova attività" icon={Plus} testid="haccp-task-form">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                <div className="col-span-2">
                  <label className={labelCls}>Attività</label>
                  <input value={taskForm.nome} onChange={(e) => setTaskForm({ ...taskForm, nome: e.target.value })}
                    placeholder="es. Sanificazione banco" className={inputCls} data-testid="haccp-task-nome" />
                </div>
                <div>
                  <label className={labelCls}>Zona</label>
                  <input value={taskForm.zona} onChange={(e) => setTaskForm({ ...taskForm, zona: e.target.value })}
                    placeholder="es. Cucina" className={inputCls} data-testid="haccp-task-zona" />
                </div>
                <div>
                  <label className={labelCls}>Frequenza</label>
                  <select value={taskForm.frequenza} onChange={(e) => setTaskForm({ ...taskForm, frequenza: e.target.value })}
                    className={inputCls} data-testid="haccp-task-freq">
                    <option value="giornaliera">Giornaliera</option>
                    <option value="settimanale">Settimanale</option>
                    <option value="mensile">Mensile</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Prodotto/metodo (facoltativo)</label>
                  <input value={taskForm.prodotto} onChange={(e) => setTaskForm({ ...taskForm, prodotto: e.target.value })}
                    placeholder="es. Sgrassatore alcalino" className={inputCls} data-testid="haccp-task-prodotto" />
                </div>
              </div>
              <button onClick={addTask} data-testid="haccp-task-add"
                className="mt-3 h-11 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                <Plus size={14} weight="bold" /> Aggiungi attività
              </button>
            </Card>
          )}
          <Card title="Piano delle pulizie" icon={Broom} testid="haccp-tasks">
            <div className="divide-y divide-zinc-100">
              {tasks.map((t) => (
                <div key={t.id} className="py-2.5 flex items-center gap-2" data-testid={`haccp-task-row-${t.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                      {t.nome}
                      <span className={`text-[10px] font-bold px-1.5 py-px rounded-full ${FREQ_BADGE[t.frequenza]}`}>{t.frequenza}</span>
                    </div>
                    <div className="text-xs text-zinc-500">{[t.zona, t.prodotto].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {isManager && (
                    <button onClick={() => del(`/haccp/cleaning/tasks/${t.id}`, loadTasks, "Attività eliminata")}
                      className="h-9 w-9 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center"
                      data-testid={`haccp-task-del-${t.id}`}><Trash size={15} /></button>
                  )}
                </div>
              ))}
              {tasks.length === 0 && <p className="text-sm text-zinc-400">Nessuna attività.</p>}
            </div>
          </Card>
          <Card title="Ultime pulizie registrate" icon={Check} testid="haccp-clean-logs">
            <div className="divide-y divide-zinc-100">
              {cleanLogs.slice(0, 20).map((l) => (
                <div key={l.id} className="py-2 flex items-center gap-2 text-sm">
                  <span className="text-zinc-500 text-xs w-24 shrink-0">{fmtDT(l.at)}</span>
                  <span className="flex-1 font-semibold text-zinc-900">{l.task_nome}</span>
                  <span className="text-xs text-zinc-400">{l.by}</span>
                </div>
              ))}
              {cleanLogs.length === 0 && <p className="text-sm text-zinc-400">Nessuna registrazione.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === "merce" && (
        <div className="space-y-4" data-testid="haccp-merce">
          <Card title="Registra controllo merce in entrata" icon={Package} testid="haccp-goods-form">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Fornitore *</label>
                <input value={goodsForm.fornitore} onChange={(e) => setGoodsForm({ ...goodsForm, fornitore: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-fornitore" />
              </div>
              <div>
                <label className={labelCls}>Prodotto *</label>
                <input value={goodsForm.prodotto} onChange={(e) => setGoodsForm({ ...goodsForm, prodotto: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-prodotto" />
              </div>
              <div>
                <label className={labelCls}>Lotto</label>
                <input value={goodsForm.lotto} onChange={(e) => setGoodsForm({ ...goodsForm, lotto: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-lotto" />
              </div>
              <div>
                <label className={labelCls}>DDT n.</label>
                <input value={goodsForm.ddt} onChange={(e) => setGoodsForm({ ...goodsForm, ddt: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-ddt" />
              </div>
              <div>
                <label className={labelCls}>Temp. rilevata °C</label>
                <input type="number" step="0.5" value={goodsForm.temperatura}
                  onChange={(e) => setGoodsForm({ ...goodsForm, temperatura: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-temp" />
              </div>
              <div>
                <label className={labelCls}>Esito</label>
                <select value={goodsForm.esito} onChange={(e) => setGoodsForm({ ...goodsForm, esito: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-esito">
                  <option value="conforme">Conforme</option>
                  <option value="non_conforme">Non conforme</option>
                </select>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <label className={labelCls}>Note</label>
                <input value={goodsForm.nota} onChange={(e) => setGoodsForm({ ...goodsForm, nota: e.target.value })}
                  className={inputCls} data-testid="haccp-goods-nota" />
              </div>
            </div>
            <button onClick={addGoods} data-testid="haccp-goods-add"
              className="mt-3 h-11 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
              <Plus size={14} weight="bold" /> Registra controllo
            </button>
          </Card>
          <Card title="Merce registrata" icon={Package} testid="haccp-goods-list">
            <div className="divide-y divide-zinc-100">
              {goods.slice(0, 30).map((g) => (
                <div key={g.id} className="py-2 flex items-center gap-2 text-sm" data-testid={`haccp-goods-row-${g.id}`}>
                  <span className="text-zinc-500 text-xs w-24 shrink-0">{fmtDT(g.at)}</span>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-zinc-900">{g.prodotto}</span>
                    <span className="text-zinc-500 text-xs"> · {g.fornitore}{g.lotto ? ` · lotto ${g.lotto}` : ""}{g.temperatura != null ? ` · ${g.temperatura}°C` : ""}</span>
                  </div>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${g.esito === "non_conforme" ? "bg-rose-600 text-white" : "bg-emerald-100 text-emerald-800"}`}>
                    {g.esito === "non_conforme" ? "NON CONFORME" : "CONFORME"}
                  </span>
                </div>
              ))}
              {goods.length === 0 && <p className="text-sm text-zinc-400">Nessun controllo registrato.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === "scadenze" && (
        <div className="space-y-4" data-testid="haccp-scadenze">
          <Card title="Aggiungi prodotto allo scadenziario" icon={Plus} testid="haccp-prod-form">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <div className="col-span-2">
                <label className={labelCls}>Prodotto *</label>
                <input value={prodForm.nome} onChange={(e) => setProdForm({ ...prodForm, nome: e.target.value })}
                  className={inputCls} data-testid="haccp-prod-nome" />
              </div>
              <div>
                <label className={labelCls}>Scadenza *</label>
                <input type="date" value={prodForm.scadenza} onChange={(e) => setProdForm({ ...prodForm, scadenza: e.target.value })}
                  className={inputCls} data-testid="haccp-prod-scadenza" />
              </div>
              <div>
                <label className={labelCls}>Lotto</label>
                <input value={prodForm.lotto} onChange={(e) => setProdForm({ ...prodForm, lotto: e.target.value })}
                  className={inputCls} data-testid="haccp-prod-lotto" />
              </div>
              <div>
                <label className={labelCls}>Posizione</label>
                <input value={prodForm.posizione} onChange={(e) => setProdForm({ ...prodForm, posizione: e.target.value })}
                  placeholder="es. Frigo 1" className={inputCls} data-testid="haccp-prod-pos" />
              </div>
            </div>
            <button onClick={addProduct} data-testid="haccp-prod-add"
              className="mt-3 h-11 px-5 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
              <Plus size={14} weight="bold" /> Aggiungi prodotto
            </button>
          </Card>
          <Card title="Scadenziario" icon={Warning} testid="haccp-prod-list">
            <div className="divide-y divide-zinc-100">
              {products.map((p) => {
                const oggiStr = new Date().toISOString().slice(0, 10);
                const stato = p.scadenza < oggiStr ? "scaduto"
                  : p.scadenza <= new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10) ? "urgente" : "in_scadenza";
                return (
                  <div key={p.id} className="py-2 flex items-center gap-2" data-testid={`haccp-prod-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-900">{p.nome}</div>
                      <div className="text-xs text-zinc-500">scade {fmtD(p.scadenza)}{p.lotto ? ` · lotto ${p.lotto}` : ""}{p.posizione ? ` · ${p.posizione}` : ""}</div>
                    </div>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${SCAD_BADGE[stato]}`}>{SCAD_LABEL[stato]}</span>
                    <button onClick={() => del(`/haccp/products/${p.id}`, loadProducts, "Prodotto eliminato")}
                      className="h-9 w-9 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center"
                      data-testid={`haccp-prod-del-${p.id}`}><Trash size={15} /></button>
                  </div>
                );
              })}
              {products.length === 0 && <p className="text-sm text-zinc-400">Nessun prodotto.</p>}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
